import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiMail,
  FiSend,
  FiMessageSquare,
  FiPlayCircle,
  FiSettings,
  FiClock,
  FiFacebook,
  FiInstagram,
  FiCheckCircle,
  FiLoader,
} from 'react-icons/fi';
import { Reveal } from '../components/ui';
import { moduleCount } from '../data/product';

const SUBJECTS = [
  'Book a demo',
  'Pricing and plans',
  'Moving off spreadsheets',
  'Technical support',
  'Partnership',
  'Something else',
];

/** What actually happens after the form is sent — sets expectations honestly. */
const NEXT_STEPS = [
  {
    icon: FiMessageSquare,
    title: 'We read it, and reply',
    desc: 'A real person, usually within one working day. No drip sequence.',
  },
  {
    icon: FiPlayCircle,
    title: 'A walkthrough on your terms',
    desc: `A short call through whichever of the ${moduleCount} modules matter to your firm.`,
  },
  {
    icon: FiSettings,
    title: 'Your own workspace',
    desc: 'We set up an isolated workspace with your projects so you can test it properly.',
  },
];

const FIELD =
  'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-ink-800 placeholder:text-slate-400 transition-all focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15';

const LABEL = 'mb-2 block text-sm font-semibold text-ink-700';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formsubmit.co/buildflow.team@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          // Fall back to a readable subject when the dropdown is left untouched,
          // otherwise the email arrives with a trailing colon and nothing after it.
          _subject: `BuildFlow enquiry: ${formData.subject || 'General'}`,
          _captcha: 'false',
          _replyto: formData.email,
          _template: 'table',
        }),
      });

      if (response.ok) {
        toast.success("Message sent. We'll get back to you shortly.");
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setSent(true);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative bg-ink-900 pb-28 pt-32 sm:pt-40">
        <div className="absolute inset-0 blueprint-grid mask-fade opacity-70" aria-hidden />
        <div
          className="absolute left-1/2 top-4 h-72 w-72 -translate-x-1/2 rounded-full bg-primary-600/25 blur-3xl"
          aria-hidden
        />
        <div className="container-custom relative">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <span className="pill-invert mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                Talk to us
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl">
                Let's get your firm{' '}
                <span className="text-gradient-animate">out of spreadsheets</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mx-auto mt-5 max-w-2xl lede-invert">
                Tell us how your practice runs today and we'll show you the parts of BuildFlow that
                would change it. No obligation, no card, and no sales script.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Form + sidebar, pulled up over the dark hero */}
      <section className="relative -mt-20 pb-8">
        <div className="container-custom">
          <div className="grid gap-6 lg:grid-cols-5">
            {/* Form */}
            <Reveal className="lg:col-span-3">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-card-hover sm:p-8">
                <div className="mb-6 flex items-start gap-3">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-primary-600 to-accent-500 text-white shadow-glow">
                    <FiSend className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="font-display text-xl font-bold text-ink-800">
                      Send us a message
                    </h2>
                    <p className="text-sm text-slate-500">
                      Fields marked with an asterisk are required.
                    </p>
                  </div>
                </div>

                {sent && (
                  <div className="mb-6 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <FiCheckCircle className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                    <p className="text-sm text-emerald-800">
                      Thanks — your message is with us. You'll hear back from a real person, usually
                      within one working day.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className={LABEL}>
                        Full name *
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={FIELD}
                        placeholder="Ananya Rao"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className={LABEL}>
                        Work email *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={FIELD}
                        placeholder="you@yourfirm.com"
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="phone" className={LABEL}>
                        Phone
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className={FIELD}
                        placeholder="+91 98765 43210"
                      />
                      <p className="mt-1.5 text-xs text-slate-500">
                        Optional — only if you'd rather we called.
                      </p>
                    </div>
                    <div>
                      <label htmlFor="subject" className={LABEL}>
                        What's this about?
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className={`${FIELD} appearance-none bg-white`}
                      >
                        <option value="">Select a topic</option>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className={LABEL}>
                      Your message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className={FIELD}
                      placeholder="How many projects do you run at once? How do you track attendance and vendor bills today?"
                    />
                    <p className="mt-1.5 text-xs text-slate-500">
                      The more you tell us about how your firm works, the more useful the first call
                      will be.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:from-primary-500 hover:to-accent-500 hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <FiLoader className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send message
                        <FiArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-500">
                    We use this only to reply to you. Read our{' '}
                    <Link to="/privacy" className="font-medium text-primary-600 hover:underline">
                      privacy policy
                    </Link>
                    .
                  </p>
                </form>
              </div>
            </Reveal>

            {/* Sidebar */}
            <div className="space-y-6 lg:col-span-2">
              <Reveal delay={0.08}>
                <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-card sm:p-7">
                  <h3 className="eyebrow mb-5 text-primary-600">
                    <span className="h-1 w-1 rounded-full bg-primary-500" />
                    What happens next
                  </h3>
                  <ol className="space-y-5">
                    {NEXT_STEPS.map((s, i) => (
                      <li key={s.title} className="flex gap-3.5">
                        <span className="relative flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                          <s.icon className="h-4 w-4" />
                          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
                            {i + 1}
                          </span>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-ink-800">{s.title}</p>
                          <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>

              <Reveal delay={0.14}>
                <div className="relative overflow-hidden rounded-3xl bg-ink-900 p-6 text-white sm:p-7">
                  <div className="absolute inset-0 blueprint-grid opacity-50" aria-hidden />
                  <div className="relative">
                    <h3 className="eyebrow mb-5 text-accent-300">
                      <span className="h-1 w-1 rounded-full bg-accent-300" />
                      Reach us directly
                    </h3>

                    <a
                      href="mailto:buildflow.team@gmail.com"
                      className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 transition-all hover:border-accent-400/40 hover:bg-white/[0.08]"
                    >
                      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-primary-600 to-accent-500 text-white">
                        <FiMail className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-xs text-slate-400">Email</span>
                        <span className="block truncate text-sm font-semibold text-white group-hover:text-accent-300">
                          buildflow.team@gmail.com
                        </span>
                      </span>
                    </a>

                    <div className="mt-3 flex items-center gap-2.5 px-1 text-xs text-slate-400">
                      <FiClock className="h-3.5 w-3.5 flex-none" />
                      Monday to Saturday, 10am – 7pm IST
                    </div>

                    <div className="mt-6 border-t border-white/10 pt-5">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Follow along
                      </p>
                      <div className="flex gap-2.5">
                        <a
                          href="https://www.facebook.com/profile.php?id=61574392703440"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="BuildFlow on Facebook"
                          className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 transition-all hover:border-accent-400/40 hover:bg-white/10 hover:text-white"
                        >
                          <FiFacebook className="h-4 w-4" />
                        </a>
                        <a
                          href="https://www.instagram.com/the.buildflow/"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="BuildFlow on Instagram"
                          className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 transition-all hover:border-accent-400/40 hover:bg-white/10 hover:text-white"
                        >
                          <FiInstagram className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="rounded-3xl border border-primary-100 bg-primary-50/60 p-6 sm:p-7">
                  <p className="text-sm font-semibold text-ink-800">
                    Just want to know the price?
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                    All {moduleCount} modules, three plans, from ₹999 a month. It's all on the
                    pricing page — no form required.
                  </p>
                  <Link
                    to="/pricing"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
                  >
                    See pricing
                    <FiArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
